<script setup lang="ts">
import { cn } from "@lib/utils"
import { X } from "lucide-vue-next"
import { DialogClose, DialogContent, type DialogContentEmits, type DialogContentProps, DialogOverlay, DialogPortal, useEmitAsProps } from "radix-vue"

const props = defineProps<DialogContentProps & { class?: string }>()
const emits = defineEmits<DialogContentEmits>()

const emitsAsProps = useEmitAsProps(emits)

function onInteractOutside(event: Event) {
    const originalEvent = (event as CustomEvent<{ originalEvent: Event }>).detail?.originalEvent
    const target = (originalEvent?.target ?? event.target) as HTMLElement | null

    if (
        target?.closest("[data-radix-popover-content]") ||
        target?.closest("[data-radix-popper-content-wrapper]") ||
        target?.closest("[cmdk-root]")
    ) {
        event.preventDefault()
    }
}
</script>

<template>
    <DialogPortal>
        <DialogOverlay
            class="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        />
        <DialogContent
            :class="
                cn(
                    'fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border border-border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg md:w-full',
                    props.class
                )
            "
            v-bind="{ ...props, ...emitsAsProps }"
            @pointer-down-outside="onInteractOutside"
            @focus-outside="onInteractOutside"
            @interact-outside="onInteractOutside"
        >
            <slot />

            <DialogClose class="absolute top-3 right-3 p-0.5 transition-colors rounded-md hover:bg-secondary">
                <X class="w-4 h-4" />
                <span class="sr-only">Close</span>
            </DialogClose>
        </DialogContent>
    </DialogPortal>
</template>
